import { extractYouTubeId, getChannelName, getYouTubeVideoId } from '@lib/utils/platforms';
import { formatBytesToString, formatDurationToHHMMSS } from '@lib/utils/units';
import { getMediaDuration, isProbablyImage } from '@pages/Library/utils/mediaStatus';
import React from 'react';
import css from '../LibraryContentMediaTable.module.scss';
import { renderWithUploadingCheck } from './cellUtils';
import { HighlightedText } from './HighlightedText';
import { CellProps } from './types';

// Width Cell
export const WidthCell = React.memo<CellProps>(({ media }) => {
  return renderWithUploadingCheck(
    media,
    () => {
      const width = media.metadata?.video?.width || 0;
      return <span className={css.widthCell}>{width || '—'}</span>;
    },
    <span className={css.widthCell}>—</span>,
  );
});

WidthCell.displayName = 'WidthCell';

// Height Cell
export const HeightCell = React.memo<CellProps>(({ media }) => {
  return renderWithUploadingCheck(
    media,
    () => {
      const height = media.metadata?.video?.height || 0;
      return <span className={css.heightCell}>{height || '—'}</span>;
    },
    <span className={css.heightCell}>—</span>,
  );
});

HeightCell.displayName = 'HeightCell';

// Frame Count Cell
export const FrameCountCell = React.memo<CellProps>(({ media }) => {
  return renderWithUploadingCheck(
    media,
    () => {
      const frameCount = media.metadata?.video?.frameCount || 0;
      return <span className={css.frameCountCell}>{frameCount.toLocaleString()}</span>;
    },
    <span className={css.frameCountCell}>-</span>,
  );
});

FrameCountCell.displayName = 'FrameCountCell';

// Duration Cell
export const DurationCell = React.memo<CellProps>(({ media }) => {
  return renderWithUploadingCheck(
    media,
    () => {
      if (isProbablyImage(media)) {
        return <span className={css.durationCell}>—</span>;
      }
      const duration = getMediaDuration(media);
      return (
        <span className={css.durationCell}>
          {duration ? formatDurationToHHMMSS(duration) : '—'}
        </span>
      );
    },
    <span className={css.durationCell}>—</span>,
  );
});

DurationCell.displayName = 'DurationCell';

// Size Cell (originalMediaSize is always in MB from backend and virtual upload rows)
export const SizeCell = React.memo<CellProps>(({ media }) => {
  return renderWithUploadingCheck(
    media,
    () => {
      const sizeInMb = media.metadata?.video?.originalMediaSize || 0;
      const sizeInBytes = sizeInMb * 1024 * 1024;
      return (
        <span className={css.sizeCell}>{!sizeInMb ? '—' : formatBytesToString(sizeInBytes)}</span>
      );
    },
    <span className={css.sizeCell}>
      {!media.metadata?.video?.originalMediaSize
        ? '—'
        : formatBytesToString((media.metadata?.video?.originalMediaSize || 0) * 1024 * 1024)}
    </span>,
  );
});

SizeCell.displayName = 'SizeCell';

// Channel Cell
export const ChannelCell = React.memo<CellProps>(({ media }) => {
  if (media.status === 'uploading' || media.status === 'queued') {
    return <span className={css.channelCell}>Uploading</span>;
  }

  return <span className={css.channelCell}>{getChannelName(media)}</span>;
});

ChannelCell.displayName = 'ChannelCell';

// A date, with its time on a quieter line beneath it. A source that only knows the day stores it
// as midnight UTC, which would read as a made-up local time ("3:00 AM"), so that one goes without.
const DateTimeText = ({ date }: { date: Date }) => {
  const dayOnly = date.getTime() % 86_400_000 === 0;
  return (
    <div className={css.dateCell}>
      <div className={css.dateText}>
        {date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
      </div>
      {!dayOnly && (
        <div className={css.timeText}>
          {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </div>
      )}
    </div>
  );
};

// Date Added Cell
export const DateAddedCell = React.memo<CellProps>(({ media }) => {
  return renderWithUploadingCheck(
    media,
    () => <DateTimeText date={new Date(media.createdAt)} />,
    <DateTimeText date={new Date()} />,
  );
});

DateAddedCell.displayName = 'DateAddedCell';

// Published Date Cell
export const PublishedDateCell = React.memo<CellProps>(({ media }) => {
  return renderWithUploadingCheck(
    media,
    () => {
      const publishedAt = media.publishedAt;
      if (!publishedAt) {
        return (
          <div className={css.dateCell}>
            <div className={css.dateText}>—</div>
          </div>
        );
      }
      return <DateTimeText date={new Date(publishedAt)} />;
    },
    <div className={css.dateCell}>
      <div className={css.dateText}>—</div>
    </div>,
  );
});

PublishedDateCell.displayName = 'PublishedDateCell';

// YouTube Short Cell
export const IsYouTubeShortCell = React.memo<CellProps>(({ media }) => {
  const isYouTubeShort = media.userMetadata?.isYoutubeShort === true;
  return <span className={css.isYouTubeShortCell}>{isYouTubeShort ? 'Yes' : 'No'}</span>;
});

IsYouTubeShortCell.displayName = 'IsYouTubeShortCell';

// YouTube ID Cell
export const YouTubeIdCell = React.memo<CellProps & { highlightTerms?: string[] }>(
  ({ media, highlightTerms = [] }) => {
    const youtubeId = getYouTubeVideoId(media);
    if (!youtubeId) return <span className={css.youtubeIdCell}>—</span>;

    if (highlightTerms.length === 0) {
      return <span className={css.youtubeIdCell}>{youtubeId}</span>;
    }

    const tokens = highlightTerms.flatMap((raw) => raw.split(/\s+/).filter(Boolean));
    const hasExactMatch = tokens.some((token) => extractYouTubeId(token) === youtubeId);

    if (hasExactMatch) {
      return (
        <span className={css.youtubeIdCell}>
          <span className={css.filterHighlight}>{youtubeId}</span>
        </span>
      );
    }

    const substrings = tokens.filter((token) => {
      const id = extractYouTubeId(token);
      return !id && token.length > 0 && youtubeId.includes(token);
    });

    return (
      <span className={css.youtubeIdCell}>
        <HighlightedText text={youtubeId} terms={substrings} caseSensitive />
      </span>
    );
  },
);

YouTubeIdCell.displayName = 'YouTubeIdCell';
